import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { type Invitation, type Member, type Role, type Sent, Stewardship } from '@accounts/domain';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Unavailable, useConfirm, Waiting } from '@beyond-js/ui/react';
import { Go, Outage, Problem, useLast, useQuestions } from '@accounts/ui';
import { Audit } from './organization/audit';
import { Closed, Closing } from './organization/closing';
import { Danger } from './organization/danger';
import { OrganizationFilters } from './organization/filters';
import { lines } from './organization/holdings';
import { Invitations } from './organization/invitations';
import { Invite } from './organization/invite';
import { Members } from './organization/members';
import { useProducts } from './organization/products';
import { Region } from './organization/region';
import { useRequests } from './organization/requests';
import { RoleDialog } from './organization/role';
import { Secret } from './organization/secret';
import { Settings } from './organization/settings';

/**
 * One organization: its members, its invitations, its settings, its activity and, for an owner, handing
 * it over and deleting it.
 *
 * Every refusal the service can give is shown as itself (`Problem`). `?view=members|invitations|
 * settings|audit` selects one visible region and moves focus there. The team is one region: the members,
 * then, for owners and administrators, the invitations of people who have not joined, each in its own
 * section; `?view=invitations` opens the team at its invitations. Hidden forms stay mounted so changing
 * regions preserves drafts. Settings owns identity and lifecycle actions. Unknown views, including the
 * retired `?view=github`, open the team here. Reading ends in the members, in a refusal, or in
 * Accounts not answering, said once for the page (`Outage`, D163): as its body while nothing was read,
 * else as a line, the members and invitations kept from the last read saying when that was. The role
 * requests and the activity while shown, and the deletion's holdings join the same notice, and
 * nothing is offered that cannot work while nothing was read. Only the latest read is shown. An organization in its
 * grace before deletion is shown to its members as such, with **Cancel deletion** for an owner (`Closing`).
 */
export function Organization() {
	const { organization = '' } = useParams();
	const { text, identity, client, accounts, organizations, refresh, renewed, language } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const [parameters, setParameters] = useSearchParams();
	const filters = new OrganizationFilters(parameters);
	const navigate = useNavigate();
	const view = parameters.get('view');
	const region = ['members', 'invitations', 'settings', 'audit'].includes(view ?? '') ? view! : 'members';
	// The team holds the members and the invitations
	const team = region === 'members' || region === 'invitations';
	const arrived = useLocation().state as { created?: boolean } | null;

	const [members, setMembers] = useState<Member[]>();
	const [invitations, setInvitations] = useState<Invitation[]>();
	// When they were last read, said over them while a later read does not answer
	const [at, setAt] = useState<number>();
	const all = filters.removed;
	const [reading, setReading] = useState(true);
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [notice, setNotice] = useState<string | undefined>(() => (arrived?.created ? text('organizations.created') : undefined));
	const [changing, setChanging] = useState<{ member: Member; role?: Role }>();
	const latest = useRef(0);
	// The change sent last, which the person may send again when it was not made (D186)
	const last = useLast();
	const [unsent, setUnsent] = useState<string>();
	const [inviting, setInviting] = useState(false);
	const [sent, setSent] = useState<Sent[]>();
	const withdrawn = filters.closed;
	const { confirm } = useConfirm(useQuestions());

	const filter = (name: 'removed' | 'closed', enabled: boolean) => setParameters(current => new OrganizationFilters(current).set(name, enabled), { replace: true });

	const membership = identity?.organizations.find(entry => entry.organization === organization);
	const name = membership?.name ?? text('nav.organizations');
	const role = membership?.role;
	const administrator = role === 'owner' || role === 'admin';
	// The open role requests are a read of their own, which joins the page's notice (D163)
	const asked = useRequests(stewardship, organization, administrator, renewed);

	// Whether Accounts answered, which takes the read out of the page's outage; a read a later one replaced
	// did not
	const read = useCallback(async () => {
		const turn = ++latest.current;
		setReading(true);
		setUnread(Refusal.kept);
		try {
			const found = await stewardship.members(organization, all);
			const invited = administrator ? await organizations.invitations(organization, withdrawn) : undefined;
			if (turn !== latest.current) return false;
			setMembers(found);
			if (invited) setInvitations(invited);
			setAt(Date.now());
			setUnread(undefined);
			return true;
		} catch (error) {
			if (turn !== latest.current || (Refusal.is(error) && error.quiet)) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		} finally {
			if (turn === latest.current) setReading(false);
		}
	}, [administrator, all, withdrawn, organization, organizations, stewardship]);

	useEffect(() => {
		void read();
	}, [read, renewed]);
	// The products, by which the role dialog and what the products hold name them (D163)
	const products = useProducts(accounts);

	const ready = Boolean(members) && (!administrator || Boolean(invitations));
	useEffect(() => {
		if (!ready || !view || !['members', 'invitations', 'settings', 'audit'].includes(view)) return;
		const region = globalThis.document?.getElementById(view) ?? globalThis.document?.getElementById('manage');
		region?.scrollIntoView({ block: 'start' });
		region?.focus({ preventScroll: true });
	}, [ready, view]);

	/** Runs a change, says its outcome, and reads the organization again */
	/** @param again Whether no action of its own stays in view, so a failure may offer Try again */
	async function act(body: () => Promise<unknown>, done: string, target?: string, again = true) {
		if (again) last.keep(() => act(body, done, target));
		else last.clear();
		setFailure(undefined);
		setNotice(undefined);
		setUnsent(undefined);
		try {
			await body();
			await Promise.all([read(), asked.run()]);
			setNotice((await refresh()) ? done : text('state.savedStale'));
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? target : undefined);
			setFailure(error);
			if (Refusal.is(error) && error.unknown) await Promise.all([read(), asked.run(), refresh()]);
		}
	}

	/** A removal or a departure, confirmed with what the products hold for that member (ACC-14) */
	async function depart(member: Member, own: boolean) {
		const held = await stewardship.holdings(organization, member.account).catch(() => undefined);
		// What could not be read is said as such, never as nothing held (D163)
		const affected = held && products.list ? lines(held, products.list, text) : [text('holdings.away')];
		const person = member.member ?? text('members.unnamed');
		const accepted = await confirm({
			title: own ? text('leave.title', { organization: name }) : text('members.remove.title', { name: person }),
			message: own ? text('leave.body', { organization: name }) : text('members.remove.body', { organization: name }),
			accept: own ? text('leave.action') : text('members.remove.accept'),
			cancel: text('action.cancel'),
			tone: 'danger',
			consequence: {
				...(affected.length ? { affected } : {}),
				lost: text(own ? 'leave.lost' : 'members.remove.lost'),
				kept: text(own ? 'leave.kept' : 'members.remove.kept'),
				recovery: text(own ? 'leave.recovery' : 'members.remove.recovery')
			}
		});
		if (!accepted) return;
		if (!own) return act(() => organizations.remove(organization, member.account), text('members.remove.done'), member.account);
		await leave();
	}

	async function leave() {
		last.keep(leave);
		setFailure(undefined);
		try {
			await stewardship.leave(organization);
			await refresh();
			navigate('/organizations', { state: { left: name } });
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			if (Refusal.is(error) && error.unknown) await Promise.all([read(), refresh()]);
		}
	}

	async function withdraw(invitation: Invitation) {
		const accepted = await confirm({
			title: text('invitations.revoke.title', { email: invitation.email }),
			message: text('invitations.revoke.body'),
			accept: text('invitations.revoke.accept'),
			cancel: text('action.cancel'),
			tone: 'danger',
			consequence: { lost: text('invitations.revoke.lost'), kept: text('invitations.revoke.kept'), recovery: text('invitations.revoke.recovery') }
		});
		if (accepted) await act(() => organizations.withdraw(organization, invitation.id), text('invitations.revoke.done'), invitation.id);
	}

	const closed = <Closed code={unread?.code ?? null} />;
	if (!membership && !reading) return <Screen title={text('nav.organizations')}>{<Closing organization={organization} fallback={closed} />}</Screen>;
	// Not known to be theirs yet: no regions to draw, one wait until the read says (LOAD-01)
	if (!membership && !unread) return <Screen title={text('nav.organizations')}><Waiting language={language} /></Screen>;

	const me = members?.find(member => member.account === identity?.account.id);
	const owners = members?.filter(member => member.state === 'active' && member.role === 'owner').length ?? 0;
	const outage = Boolean(unread?.unreachable);
	// Nothing of it was read: the outage is the page's body, except over the settings an administrator keeps
	const whole = outage && !members && !(region === 'settings' && administrator);
	const updated = outage ? (at ?? null) : undefined;
	return (
		<Screen
			title={name}
			width={region === 'settings' ? 'form' : 'fluid'}
			crumbs={[{ label: text('nav.organizations'), href: '/organizations' }]}
			// Nothing of the organization read, nothing offered that cannot work then
			actions={administrator && !whole && team ? <Button variant="primary" label={text('invitations.send')} onClick={() => setInviting(true)} /> : undefined}
		>
			<Outage
				whole={whole}
				reads={[
					{ key: 'organization', title: text(administrator ? 'outage.organization' : 'members.title'), unread, run: read },
					{ key: 'requests', title: text('members.requests'), unread: asked.unread, run: asked.run }
				]}
			>
				{failure ? <Problem error={failure} again={last.kept ? last.again : undefined} check={() => Promise.all([read(), asked.run()])} /> : null}
				{unread && !outage && !reading ? unread.denied || unread.code === 'NOT_FOUND' ? closed : <Problem error={unread} /> : null}
				{members && asked.unread && !asked.unread.unreachable ? <Problem error={asked.unread} /> : null}
				{notice ? <Callout tone="success" live title={notice} /> : null}

				<Region shown={team}>
				{members || !unread ? (
					<Members
						members={members}
						updated={updated}
						requests={asked.requests}
						role={role}
						all={all}
						unsent={unsent}
						onAll={enabled => filter('removed', enabled)}
						onChange={(member, suggested) => setChanging({ member, role: suggested })}
						onRemove={member => void depart(member, false)}
						onLeave={() => me && void depart(me, true)}
						onDecline={request => void act(() => stewardship.decline(organization, request.id), text('members.request.declined'), undefined, false)}
					/>
				) : null}

				{administrator && (invitations || !unread) ? (
					<Invitations
						invitations={invitations}
						updated={updated}
						all={withdrawn}
						unsent={unsent}
						onAll={enabled => filter('closed', enabled)}
						onResend={invitation => void act(async () => setSent([await organizations.resend(organization, invitation.id)]), text('invitations.resent'))}
						onWithdraw={invitation => void withdraw(invitation)}
					/>
				) : null}

				</Region>

				{/* A region this role cannot open is refused in place, with the way to the members; the members
				    themselves say who manages them, quietly (D06) */}
				{!team && membership && !administrator && (members || !unread) ? (
					<div id="manage" tabIndex={-1}>
						<Unavailable
							kind="access"
							title={text('organization.manage.title')}
							reason={text('organization.manage.reason', { role: text(`role.${membership.role}`) })}
							owner={text('organization.manage.owner', { organization: name })}
							labels={Unavailable.labels[language]}
							action={<Go to={`/organizations/${encodeURIComponent(organization)}?view=members`} label={text('organization.manage.action')} />}
						/>
					</div>
				) : null}

				<Region shown={region === 'settings'}>
				{administrator && membership ? (
					<Settings
						organization={organization}
						name={membership.name ?? ''}
						slug={membership.slug}
						owner={role === 'owner'}
						logo={membership.logo}
						onSaved={async saved => {
							await refresh();
							setNotice(saved);
						}}
					/>
				) : null}

				{role === 'owner' && (members || !unread) ? (
					<Danger
						organization={organization}
						name={name}
						members={members}
						products={products}
						onDone={async saved => {
							await refresh();
							navigate('/organizations', { state: { notice: saved } });
						}}
					/>
				) : null}

				</Region>
				<Region shown={region === 'audit'}>
					{administrator && (members || !unread) ? <Audit organization={organization} shown={region === 'audit'} /> : null}
				</Region>

				<RoleDialog
					organization={organization}
					name={name}
					member={changing?.member}
					suggested={changing?.role}
					role={role}
					owners={owners}
					products={products}
					onClose={() => setChanging(undefined)}
					onChoose={async (member, chosen) => {
						setChanging(undefined);
						await act(() => organizations.assign(organization, member.account, chosen), text('members.role.done'));
					}}
				/>

				<Invite organization={organization} owner={role === 'owner'} open={inviting} onClose={() => setInviting(false)} onCreated={async created => (setInviting(false), setSent(created), await read())} onUnknown={read} />
				<Secret sent={sent} onClose={() => setSent(undefined)} />
			</Outage>
		</Screen>
	);
}
